import axios from "axios";
import { useCallback, useState } from "react";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import { CombinedListingReadiness, DCDesignVariantGroup } from "./types";

const variantGroupsUrl = `${environment.baseUrl}/api/v1/designs/variant-groups`;

// A 403 from any variant-group endpoint means the brand is not Plus and/or the
// `color_variants_combined_listings` flag is off. Callers use this to hide the UI.
const is403 = (err: any): boolean => err?.response?.status === 403;

// #region titlePushWarning
// Builds a warning message when some released members' Shopify titles couldn't be updated.
const titlePushWarning = (group: DCDesignVariantGroup | null | undefined): string | null => {
    const failures = group?.title_push_failures ?? [];
    if (failures.length === 0) return null;
    const names = failures.map(f => f.title ? `${f.title} (#${f.design_id})` : `#${f.design_id}`).join(", ");
    return `Name saved, but Shopify couldn't be updated for: ${names}`;
};

// #region useDesignVariantGroups
export const useDesignVariantGroups = () => {
    const [, setSnackbar] = useSnackbar();
    const [, authToken, loginFunctions] = useLogin();
    const [featureUnavailable, setFeatureUnavailable] = useState(false);
    const [loading, setLoading] = useState(false);

    const headers = { headers: { "X-Wallmates-Auth": authToken } };

    // #region getGroupForDesign
    // Returns the variant group a design belongs to, or null when ungrouped.
    // On a 403 the feature is unavailable for the brand → flag it and return null.
    const getGroupForDesign = useCallback(async (designId: number): Promise<DCDesignVariantGroup | null> => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${variantGroupsUrl}/by-design/${designId}`, headers);
            return (data as DCDesignVariantGroup) ?? null;
        } catch (err: any) {
            if (is403(err)) {
                setFeatureUnavailable(true);
                return null;
            }
            errorCatcher(err, loginFunctions.logout);
            return null;
        } finally {
            setLoading(false);
        }
    }, [authToken, loginFunctions.logout]);

    // #region createGroup
    const createGroup = useCallback(async (
        input: { brand_id: number; name: string; primary_design_id: number }
    ): Promise<DCDesignVariantGroup | null> => {
        try {
            const { data } = await axios.post(`${variantGroupsUrl}`, input, headers);
            return data as DCDesignVariantGroup;
        } catch (err: any) {
            if (is403(err)) {
                setFeatureUnavailable(true);
                return null;
            }
            const text = err.response?.data?.message || err.message || "Failed to create variant group";
            setSnackbar({ show: true, snackbarLevel: "error", text });
            errorCatcher(err, loginFunctions.logout);
            return null;
        }
    }, [authToken, loginFunctions.logout, setSnackbar]);

    // #region addMember
    const addMember = useCallback(async (
        groupId: number,
        design_id: number,
        color_label: string
    ): Promise<DCDesignVariantGroup | null> => {
        try {
            const { data } = await axios.post(
                `${variantGroupsUrl}/${groupId}/members`,
                { design_id, color_label },
                headers
            );
            const warning = titlePushWarning(data);
            setSnackbar(warning
                ? { show: true, snackbarLevel: "warn", text: `Variant linked. ${warning}` }
                : { show: true, snackbarLevel: "success", text: "Variant linked" });
            return data as DCDesignVariantGroup;
        } catch (err: any) {
            if (is403(err)) {
                setFeatureUnavailable(true);
                return null;
            }
            const text = err.response?.data?.message || err.message || "Failed to link variant";
            setSnackbar({ show: true, snackbarLevel: "error", text });
            errorCatcher(err, loginFunctions.logout);
            return null;
        }
    }, [authToken, loginFunctions.logout, setSnackbar]);

    // #region removeMember
    const removeMember = useCallback(async (designId: number): Promise<boolean> => {
        try {
            await axios.delete(`${variantGroupsUrl}/members/${designId}`, headers);
            setSnackbar({ show: true, snackbarLevel: "success", text: "Variant unlinked" });
            return true;
        } catch (err: any) {
            if (is403(err)) {
                setFeatureUnavailable(true);
                return false;
            }
            const text = err.response?.data?.message || err.message || "Failed to unlink variant";
            setSnackbar({ show: true, snackbarLevel: "error", text });
            errorCatcher(err, loginFunctions.logout);
            return false;
        }
    }, [authToken, loginFunctions.logout, setSnackbar]);

    // #region updateColorLabel
    // Rename an already-linked variant's color label. Ungated grouping op.
    const updateColorLabel = useCallback(async (
        designId: number,
        color_label: string | null
    ): Promise<DCDesignVariantGroup | null> => {
        try {
            const { data } = await axios.patch(
                `${variantGroupsUrl}/members/${designId}/color-label`,
                { color_label },
                headers
            );
            setSnackbar({ show: true, snackbarLevel: "success", text: "Color label updated" });
            return data as DCDesignVariantGroup;
        } catch (err: any) {
            if (is403(err)) {
                setFeatureUnavailable(true);
                return null;
            }
            const text = err.response?.data?.message || err.message || "Failed to update color label";
            setSnackbar({ show: true, snackbarLevel: "error", text });
            errorCatcher(err, loginFunctions.logout);
            return null;
        }
    }, [authToken, loginFunctions.logout, setSnackbar]);

    // #region propagateTitle
    // Propagate a base design name to every variant in the design's group. Color labels stay
    // per-member; only the shared base title changes. Returns the updated group (or null when the
    // design is standalone / the feature is unavailable).
    const propagateTitle = useCallback(async (
        designId: number,
        title: string
    ): Promise<DCDesignVariantGroup | null> => {
        try {
            const { data } = await axios.patch(
                `${variantGroupsUrl}/members/${designId}/title`,
                { title },
                headers
            );
            const warning = titlePushWarning(data);
            setSnackbar(warning
                ? { show: true, snackbarLevel: "warn", text: warning }
                : { show: true, snackbarLevel: "success", text: "All color variants updated with this name" });
            return (data as DCDesignVariantGroup) ?? null;
        } catch (err: any) {
            if (is403(err)) {
                setFeatureUnavailable(true);
                return null;
            }
            const text = err.response?.data?.message || err.message || "Failed to update variant names";
            setSnackbar({ show: true, snackbarLevel: "error", text });
            errorCatcher(err, loginFunctions.logout);
            return null;
        }
    }, [authToken, loginFunctions.logout, setSnackbar]);

    // #region setPrimary
    const setPrimary = useCallback(async (
        groupId: number,
        design_id: number
    ): Promise<DCDesignVariantGroup | null> => {
        try {
            const { data } = await axios.patch(
                `${variantGroupsUrl}/${groupId}/primary`,
                { design_id },
                headers
            );
            setSnackbar({ show: true, snackbarLevel: "success", text: "Primary variant updated" });
            return data as DCDesignVariantGroup;
        } catch (err: any) {
            if (is403(err)) {
                setFeatureUnavailable(true);
                return null;
            }
            const text = err.response?.data?.message || err.message || "Failed to set primary variant";
            setSnackbar({ show: true, snackbarLevel: "error", text });
            errorCatcher(err, loginFunctions.logout);
            return null;
        }
    }, [authToken, loginFunctions.logout, setSnackbar]);

    // #region connectCombinedListing
    // Explicit, Plus-gated action: wire the group's linked variants into a Shopify combined listing.
    // A 403 means the brand isn't eligible — the response carries a readiness breakdown so the
    // caller can open the eligibility modal (do NOT set featureUnavailable; grouping stays open).
    // Returns { ok, needsUpgrade, readiness } so callers can react.
    const connectCombinedListing = useCallback(async (
        groupId: number
    ): Promise<{ ok: boolean; needsUpgrade?: boolean; readiness?: CombinedListingReadiness }> => {
        try {
            await axios.post(`${variantGroupsUrl}/${groupId}/combined-listing`, {}, headers);
            setSnackbar({ show: true, snackbarLevel: "success", text: "Combined listing connected in Shopify" });
            return { ok: true };
        } catch (err: any) {
            if (is403(err)) {
                // Readiness is embedded in the 403 body; fall back to an all-false breakdown.
                const readiness: CombinedListingReadiness = err.response?.data?.readiness ?? {
                    plusActive: false, planName: null, combinedListingsConfirmed: false, canConnect: false
                };
                return { ok: false, needsUpgrade: true, readiness };
            }
            const text = err.response?.data?.message || err.message || "Failed to connect combined listing";
            setSnackbar({ show: true, snackbarLevel: "error", text });
            errorCatcher(err, loginFunctions.logout);
            return { ok: false };
        }
    }, [authToken, loginFunctions.logout, setSnackbar]);

    // #region getCombinedListingReadiness
    // Fetch the eligibility breakdown for a brand (used to (re)populate the modal independently).
    const getCombinedListingReadiness = useCallback(async (
        brandId: number
    ): Promise<CombinedListingReadiness | null> => {
        try {
            const { data } = await axios.get(
                `${variantGroupsUrl}/combined-listing/readiness`,
                { ...headers, params: { brand_id: brandId } }
            );
            return data as CombinedListingReadiness;
        } catch (err: any) {
            errorCatcher(err, loginFunctions.logout);
            return null;
        }
    }, [authToken, loginFunctions.logout]);

    return {
        loading,
        featureUnavailable,
        getGroupForDesign,
        createGroup,
        addMember,
        removeMember,
        updateColorLabel,
        propagateTitle,
        setPrimary,
        connectCombinedListing,
        getCombinedListingReadiness
    };
};
